import { useState, useEffect, useMemo } from 'react'
import { updateAircraft } from '../lib/api'
import { DEPT_COLORS, darkenHex } from '../lib/deptColors'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const STATUS_STYLES = {
  'TBD':       'bg-yellow-100 text-yellow-800',
  'Scheduled': 'bg-yellow-100 text-yellow-700',
  'In Work':   'bg-blue-100 text-blue-700',
  'Complete':  'bg-green-100 text-green-700',
}

const GROUP_CONFIG = [
  { key: 'In Work',   label: 'In Work',   defaultOpen: true,  labelStyle: 'bg-blue-100 text-blue-700' },
  { key: 'Scheduled', label: 'Scheduled', defaultOpen: true,  labelStyle: 'bg-yellow-100 text-yellow-700' },
  { key: 'TBD',       label: 'TBD',       defaultOpen: true,  labelStyle: 'bg-yellow-100 text-yellow-800' },
  { key: 'Complete',  label: 'Complete',  defaultOpen: false, labelStyle: 'bg-green-100 text-green-700' },
]

function formatDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  })
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-800">{value || '—'}</span>
    </div>
  )
}

function SubtaskGroups({ subtasks, onEdit, onDelete, canEditSubtasks }) {
  const { profile } = useAuth()
  const orgDeptMap = useMemo(() => Object.fromEntries((profile?.orgs?.settings?.departments ?? []).map(d => [d.key, d])), [profile?.orgs?.id])
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {}
    GROUP_CONFIG.forEach(g => { initial[g.key] = g.defaultOpen })
    return initial
  })

  const toggleGroup = (key) => {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className="space-y-2">
      {GROUP_CONFIG.map(group => {
        const groupSubtasks = subtasks.filter(s => s.status === group.key)
        if (groupSubtasks.length === 0) return null
        const isOpen = openGroups[group.key]

        return (
          <div key={group.key} className="rounded-lg border border-gray-100 overflow-hidden">
            <button
              onClick={() => toggleGroup(group.key)}
              className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <svg
                  className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${group.labelStyle}`}>
                  {group.label}
                </span>
                <span className="text-xs text-gray-400">{groupSubtasks.length}</span>
              </div>
            </button>

            {isOpen && (
              <div className="divide-y divide-gray-50">
                {groupSubtasks.map(s => {
                  const deptHex = orgDeptMap[s.department]?.hex || '#9ca3af'
                  return (
                    <div key={s.id} className="flex items-center justify-between px-3 py-2.5 bg-white group hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: deptHex }} />
                        <div className="min-w-0">
                          <span className="text-sm text-gray-700 truncate block">{s.task_name}</span>
                          <span className="text-xs text-gray-400">
                            {formatDate(s.start_date)} → {formatDate(s.end_date)}
                          </span>
                        </div>
                      </div>
                      {canEditSubtasks && (
                        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => onEdit(s)}
                            className="p-1.5 text-gray-300 hover:text-brand-red transition-colors"
                            title="Edit subtask"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {onDelete && (
                            <button
                              onClick={() => onDelete(s)}
                              className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                              title="Delete subtask"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function ColorPicker({ aircraft, aircraftId, onColorSaved }) {
  const [color, setColor] = useState(aircraft?.color || '#3b82f6')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const save = async () => {
    setSaving(true)
    await updateAircraft(aircraftId, { color })
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
    onColorSaved && onColorSaved()
  }

  const reset = async () => {
    setSaving(true)
    await updateAircraft(aircraftId, { color: null })
    setSaving(false)
    setColor('#3b82f6')
    onColorSaved && onColorSaved()
  }

  return (
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={color}
        onChange={e => { setColor(e.target.value); setSaved(false) }}
        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
      />
      <div className="flex-1 min-w-0">
        <span className="text-sm text-gray-700 block">Project bar color</span>
        <span className="text-xs text-gray-400">Applies across all department views</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {aircraft?.color && (
          <button onClick={reset} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
            Reset
          </button>
        )}
        <button
          onClick={save}
          disabled={saving}
          className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
            saved ? 'bg-green-100 text-green-700' : 'bg-brand-red hover:bg-brand-red-dark text-white'
          }`}
        >
          {saving ? 'Saving…' : saved ? 'Saved!' : 'Save'}
        </button>
      </div>
    </div>
  )
}

function fmtTs(ts) {
  return new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function DetailDrawer({ item, subtasks, subtaskTechMap = {}, projectTechMap = {}, isEditor, canEditSubtasks, onWorkOrderClick, onClose, onHistory, onSubtaskHistory, onEdit, onAddSubtask, onDelete, onColorSaved }) {
  const { type, data } = item
  const isProject = type === 'project'
  const { isTechnician, profile } = useAuth()
  const orgDeptMap = useMemo(() => Object.fromEntries((profile?.orgs?.settings?.departments ?? []).map(d => [d.key, d])), [profile?.orgs?.id])

  const [subtaskNotes, setSubtaskNotes] = useState([])
  useEffect(() => {
    if (isProject || !data?.id) { setSubtaskNotes([]); return }
    supabase.from('project_notes').select('*').eq('subtask_id', data.id).order('created_at', { ascending: false })
      .then(({ data: notes }) => setSubtaskNotes(notes ?? []))
  }, [data?.id, isProject])
  const depts = isProject
    ? (data.departments || ['MX'])
    : (data.departments?.length ? data.departments : [data.department])
  const dept = depts[0]
  const color = { hex: orgDeptMap[dept]?.hex || '#9ca3af' }
  const projectSubtasks = isProject ? subtasks.filter(s => s.project_id === data.id) : []

  const title = isProject
    ? (data.aircraft?.registration || data.aircraft_registration || '—')
    : data.task_name

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-2 h-10 rounded-full" style={{ backgroundColor: color?.hex }} />
            <div>
              <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">
                {isProject ? 'Project' : 'Subtask'}
              </div>
              <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            </div>
          </div>
          <div className="flex items-center gap-1 mt-1">
            {/* History button */}
            {isProject && onHistory && !isTechnician && (
              <button
                onClick={() => onHistory(data.id, title)}
                title="View edit history"
                className="p-1.5 text-gray-400 hover:text-brand-red hover:bg-red-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
            {!isProject && onSubtaskHistory && !isTechnician && (
              <button
                onClick={() => onSubtaskHistory(data)}
                title="View task history"
                className="p-1.5 text-gray-400 hover:text-brand-red hover:bg-red-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
            {!isProject && canEditSubtasks && onEdit && (
              <button
                onClick={() => onEdit('subtask', data)}
                title="Edit subtask"
                className="p-1.5 text-gray-400 hover:text-brand-red hover:bg-red-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            {!isProject && canEditSubtasks && onDelete && (
              <button
                onClick={() => onDelete(data, 'subtask')}
                title="Delete subtask"
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Status + dept badges */}
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLES[data.status] || STATUS_STYLES['Scheduled']}`}>
              {data.status}
            </span>
            {depts.map(d => {
              const hex = orgDeptMap[d]?.hex || '#9ca3af'
              return (
                <span key={d} className="text-xs font-medium px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: hex + '33', color: darkenHex(hex) }}>
                  {d}
                </span>
              )
            })}
          </div>

          {/* Project or subtask fields */}
          <div className="grid grid-cols-2 gap-4">
            {isProject ? (
              <>
                <Row label="Aircraft" value={[data.aircraft?.registration || data.aircraft_registration, data.secondary_registration].filter(Boolean).join(' / ') || '—'} />
                <Row label="Status" value={data.status} />
                <Row label="Arrival" value={formatDate(data.arrival_date)} />
                <Row label="Departure" value={formatDate(data.departure_date)} />
                {data.customer_name && <Row label="Customer" value={data.customer_name} />}
                {data.work_order && (
                  onWorkOrderClick
                    ? <div className="col-span-1">
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Work Order</p>
                        <button
                          onClick={() => onWorkOrderClick(data.work_order, data.id)}
                          className="text-sm text-brand-red hover:text-brand-red-dark font-medium hover:underline"
                        >
                          {data.work_order}
                        </button>
                      </div>
                    : <Row label="Work Order" value={data.work_order} />
                )}
                {data.aircraft?.model && <Row label="Model" value={data.aircraft.model} />}
                <div className="col-span-2">
                  <Row label="Workscope" value={data.workscope} />
                </div>
              </>
            ) : (
              <>
                <Row label="Start" value={formatDate(data.start_date)} />
                <Row label="End" value={formatDate(data.end_date)} />
              </>
            )}
          </div>

          {/* Assigned Technicians — project view */}
          {isProject && (() => {
            const techs = projectTechMap[data.id] || []
            return (
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                  Technicians {techs.length > 0 && `(${techs.length})`}
                </span>
                {techs.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No technicians assigned</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {techs.map(t => (
                      <span
                        key={t.id}
                        className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t.first_name} {t.last_name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          })()}

          {/* Assigned Technicians — subtask only */}
          {!isProject && (() => {
            const techs = subtaskTechMap[data.id] || []
            return (
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                  Assigned Technicians {techs.length > 0 && `(${techs.length})`}
                </span>
                {techs.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No technicians assigned</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {techs.map(t => (
                      <span
                        key={t.id}
                        className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t.first_name} {t.last_name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          })()}

          {/* Task Notes — subtask view */}
          {!isProject && subtaskNotes.length > 0 && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                Notes ({subtaskNotes.length})
              </span>
              <div className="space-y-2">
                {subtaskNotes.map(note => (
                  <div key={note.id} className="bg-gray-50 rounded-lg px-3 py-2">
                    <div className="flex flex-wrap items-center gap-x-2 mb-0.5">
                      {note.created_by_name && (
                        <span className="text-xs font-semibold text-gray-700">{note.created_by_name}</span>
                      )}
                      <span className="text-xs text-gray-400">{fmtTs(note.created_at)}</span>
                    </div>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap leading-snug">{note.body}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subtasks grouped by status */}
          {isProject && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Subtasks ({projectSubtasks.length})
                </span>
                {canEditSubtasks && (
                  <button
                    onClick={() => onAddSubtask(data.id)}
                    className="text-xs text-brand-red hover:text-brand-red-dark font-medium"
                  >
                    + Add
                  </button>
                )}
              </div>
              {projectSubtasks.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No subtasks yet</p>
              ) : (
                <SubtaskGroups
                  subtasks={projectSubtasks}
                  canEditSubtasks={canEditSubtasks}
                  onEdit={(s) => onEdit('subtask', s)}
                  onDelete={onDelete ? (s) => onDelete(s, 'subtask') : null}
                />
              )}
            </div>
          )}

          {/* Color picker — only in department view */}
          {isProject && item.showColorPicker && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Bar Color
              </div>
              <ColorPicker
                aircraft={data.aircraft}
                aircraftId={data.aircraft_id}
                onColorSaved={onColorSaved}
              />
            </div>
          )}
        </div>

        {/* Footer — edit/delete only for editors */}
        {isEditor && (
          <div className="px-5 py-4 border-t border-gray-100 flex flex-col gap-2">
            <button
              onClick={() => onEdit(type, data)}
              className="w-full flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red-dark text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit {isProject ? 'Project' : 'Subtask'}
            </button>

            {onDelete && (
              <button
                onClick={() => onDelete(data, type)}
                className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium px-4 py-2.5 rounded-lg transition-colors border border-red-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete {isProject ? 'Project' : 'Subtask'}
              </button>
            )}
          </div>
        )}

      </div>
    </>
  )
}
